import { gql, useQuery } from '@apollo/client';
import { useToken } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';

import { StripeElements_GetStripeKeys } from './graphql-types/StripeElements_GetStripeKeys';

// this same query is in the root of the application so it should already be
// cached. by doing it this way we can avoid the loading screen that would
// otherwise occur every time we want to use stripe elements
const GET_STRIPE_KEYS = gql`
  query StripeElements_GetStripeKeys {
    getStripeKeys {
      result {
        publicKey
        connectedAccountId
      }
    }
  }
`;

export const StripeElements: React.FC<{ options?: StripeElementsOptions }> = (
  props
) => {
  const [primary500] = useToken('colors', ['primary.500']);
  const query = useQuery<StripeElements_GetStripeKeys>(GET_STRIPE_KEYS);

  const stripe = React.useMemo(() => {
    return query.data?.getStripeKeys.result?.connectedAccountId &&
      query.data?.getStripeKeys.result?.publicKey
      ? loadStripe(query.data.getStripeKeys.result.publicKey, {
          stripeAccount: query.data.getStripeKeys.result.connectedAccountId,
        })
      : null;
  }, [
    query.data?.getStripeKeys.result?.connectedAccountId,
    query.data?.getStripeKeys.result?.publicKey,
  ]);

  if (query.loading) {
    return <Loading />;
  }

  if (
    !query.data?.getStripeKeys.result?.connectedAccountId ||
    !query.data?.getStripeKeys.result?.publicKey
  ) {
    return <UnexpectedError />;
  }

  return (
    <Elements
      stripe={stripe}
      options={{
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: primary500,
          },
        },
        ...props.options,
      }}
    >
      {props.children}
    </Elements>
  );
};
